// widgets/form-widget.ts
import { showToast } from '../utils/show-toast';

interface FormWidgetOptions {
  formSelector: string;
  submitUrl: string;
  successMessage?: string | HTMLElement;
  errorMessage?: string | HTMLElement;
  beforeSubmit?: (form: HTMLFormElement) => boolean | Promise<boolean>;
  onSubmitting?: (isSubmitting: boolean) => void;
  onFetch?: () => void;
  onSuccess?: (result: unknown, response: Response) => void | Promise<void>;
  noResetAfterSubmit?: boolean;
  disableDefaultButtonHandling?: boolean;
}

export class FormWidget {
  private static readonly BOUND_ATTR = 'formWidgetBound';
  private formSelector: string;
  private submitUrl: string;
  private successMessage?: string | HTMLElement;
  private errorMessage?: string | HTMLElement;
  private beforeSubmit?: (form: HTMLFormElement) => boolean | Promise<boolean>;
  private onSubmitting?: (isSubmitting: boolean) => void;
  private onFetch?: () => void;
  private onSuccess?: (result: unknown, response: Response) => void | Promise<void>;

  private form: HTMLFormElement | null = null;
  private submitButton: HTMLButtonElement | null = null;
  private isSubmitting: boolean = false;
  private submitButtonText: string = '';
  private noResetAfterSubmit: boolean = false;
  private disableDefaultButtonHandling: boolean = false;

  constructor(options: FormWidgetOptions) {
    this.formSelector = options.formSelector;
    this.submitUrl = options.submitUrl;
    this.successMessage = options.successMessage;
    this.errorMessage = options.errorMessage;
    this.beforeSubmit = options.beforeSubmit;
    this.onSubmitting = options.onSubmitting;
    this.onFetch = options.onFetch;
    this.onSuccess = options.onSuccess;
    this.noResetAfterSubmit = options.noResetAfterSubmit ?? false;
    this.disableDefaultButtonHandling = options.disableDefaultButtonHandling ?? false;
  }

  /**
   * Инициализация, вызываем вручную
   */
  public init(initOptions?: Partial<FormWidgetOptions>) {
    if (initOptions?.successMessage) {
      this.successMessage = initOptions.successMessage;
    }
    if (initOptions?.errorMessage) {
      this.errorMessage = initOptions.errorMessage;
    }
    if (initOptions?.onFetch) {
      this.onFetch = initOptions.onFetch;
    }
    if (initOptions?.onSuccess !== undefined) {
      this.onSuccess = initOptions.onSuccess;
    }

    if (initOptions?.onSubmitting !== undefined) {
      this.onSubmitting = initOptions.onSubmitting;
    }
    if (initOptions?.beforeSubmit !== undefined) {
      this.beforeSubmit = initOptions.beforeSubmit;
    }
    if (initOptions?.disableDefaultButtonHandling !== undefined) {
      this.disableDefaultButtonHandling = initOptions.disableDefaultButtonHandling;
    }

    if (initOptions?.noResetAfterSubmit !== undefined) {
      this.noResetAfterSubmit = initOptions.noResetAfterSubmit;
    }

    this.form = document.querySelector(this.formSelector);
    this.submitButton = this.form?.querySelector('button[type="submit"]') || null;

    if (this.submitButton) {
      this.submitButtonText = this.submitButton.innerHTML;
    }

    if (!this.form) {
      return;
    }

    if (this.form.dataset[FormWidget.BOUND_ATTR] === '1') {
      return;
    }

    this.attach();
    this.form.dataset[FormWidget.BOUND_ATTR] = '1';
  }

  private attach() {
    this.form!.addEventListener('submit', (e) => {
      e.preventDefault();
      this.onSubmit(e);
    });
  }

  private setSubmittingState(isSubmitting: boolean) {
    this.isSubmitting = isSubmitting;

    // НЕ трогаем кнопку, если отключено
    if (!this.disableDefaultButtonHandling && this.submitButton) {
      if (isSubmitting) {
        this.submitButton.disabled = true;
        this.submitButton.innerHTML = 'Отправка...';
      } else {
        this.submitButton.disabled = false;
        this.submitButton.innerHTML = this.submitButtonText;
      }
    }

    if (this.onSubmitting) {
      this.onSubmitting(isSubmitting);
    }
  }

  private async onSubmit(event: Event) {
    event.preventDefault();

    if (this.isSubmitting || !this.form) return;

    // Вызываем beforeSubmit если есть
    if (this.beforeSubmit) {
      let isValid = false;
      try {
        isValid = await Promise.resolve(this.beforeSubmit(this.form));
      } catch {
        showToast(this.errorMessage || 'Ошибка обработки', 'failed');
        return;
      }

      if (!isValid) {
        return;
      }
    }

    this.setSubmittingState(true);

    try {
      const formData = new FormData(this.form);

      const response = await fetch(this.submitUrl, {
        method: 'POST',
        body: formData,
      });

      const result = (await response.json()) as {
        msg?: string;
        success?: boolean;
      };
      const businessFailed = typeof result.success === 'boolean' && result.success === false;
      const ok = response.ok && !businessFailed;

      if (!ok) {
        const msg = result.msg || this.errorMessage || 'Ошибка обработки';
        showToast(msg, 'failed');
        return;
      }

      const msg = result?.msg || this.successMessage || 'Успешно!';
      showToast(msg, 'successed');

      if (this.onSuccess) {
        await Promise.resolve(this.onSuccess(result, response));
      }

      // Сбрасываем форму только если не запрещено
      if (!this.noResetAfterSubmit) {
        this.form.reset();
      }
    } catch {
      showToast(this.errorMessage || 'Ошибка соединения', 'failed');
    } finally {
      this.setSubmittingState(false);
      if (this.onFetch) {
        this.onFetch();
      }
    }
  }
}
